/**
 * SCENE 08: THE DISSOLVE OF COMPLEXITY (Information Age // 1970 AD)
 * Overwhelming wall of complex machinery dissolves into a single silent glowing ring.
 */

import { ISceneLifecycle } from './ISceneLifecycle';
import { CameraState } from '../renderer/CameraSpline';
import { EventBus, EventType } from '../core/EventBus';
import { StateStore, StateBitmask } from '../state/Store';

export class Scene08_Dissolve implements ISceneLifecycle {
  public id = 7;
  public name = 'The Dissolve of Complexity';
  public era = 'Information Age (1970 AD)';

  private eventBus: EventBus;
  private store: StateStore;
  private isDissolved: boolean = false;
  private dissolveAlpha: number = 0;
  private unsubscribeList: Array<() => void> = [];

  constructor() {
    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();
  }

  public async preload(): Promise<void> { return Promise.resolve(); }
  public mount(): void {}

  public activate(): void {
    const unsubDown = this.eventBus.subscribe(EventType.INPUT_POINTER_DOWN, () => {
      if (!this.isDissolved) {
        this.isDissolved = true;
        this.store.setFlag(StateBitmask.RING_DISSOLVED, true);
        this.eventBus.emit(
          EventType.ACCESSIBILITY_ANNOUNCE,
          0, 0, 0, 0, 0, 1, 7,
          'All complex wires and levers dissolve into floating white light, leaving a single silent glowing ring.'
        );
      }
    });

    this.unsubscribeList.push(unsubDown);
  }

  public update(dt: number): void {
    if (this.isDissolved && this.dissolveAlpha < 1.0) {
      this.dissolveAlpha = Math.min(1.0, this.dissolveAlpha + dt * 1.8);
    }
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number, _camera: CameraState): void {
    ctx.save();
    const cx = width / 2;
    const cy = height / 2;

    if (this.isDissolved) {
      // Single Silent Glowing Ring doing all work in quiet elegance
      ctx.fillStyle = '#05070a';
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = `rgba(255, 255, 255, ${this.dissolveAlpha})`;
      ctx.lineWidth = 4;
      ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
      ctx.shadowBlur = 30;

      ctx.beginPath();
      ctx.arc(cx, cy, 100, 0, Math.PI * 2);
      ctx.stroke();

      ctx.font = '600 13px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = `rgba(255, 255, 255, ${this.dissolveAlpha})`;
      ctx.fillText('SIMPLICITY AS THE FINAL FORM', cx, cy);
    } else {
      // Chaotic Wall of Copper Wires and Machinery Levers
      ctx.fillStyle = '#14100c';
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = '#6b4d32';
      ctx.lineWidth = 2;
      for (let i = 0; i < 30; i++) {
        ctx.beginPath();
        ctx.moveTo(i * 30, 0);
        ctx.lineTo(width - i * 20, height);
        ctx.stroke();
      }

      ctx.font = '400 11px monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(240, 220, 200, 0.5)';
      ctx.fillText('[ TOUCH CENTER TO DISSOLVE COMPLEXITY ]', cx, height - 40);
    }

    ctx.restore();
  }

  public deactivate(): void {
    this.unsubscribeList.forEach(fn => fn());
    this.unsubscribeList = [];
  }
  public unmount(): void {}
  public dispose(): void {}
}
